import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { storage, db, auth } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

// 报销类型枚举
enum ReimbursementType {
    TEACHING_MATERIALS = "teaching_materials", // 教学材料
    TRAVEL = "travel", // 差旅费
    OFFICE_SUPPLIES = "office_supplies", // 办公用品
    EQUIPMENT = "equipment", // 设备
    OTHER = "other" // 其他
}

// 报销状态枚举
enum ReimbursementStatus {
    PENDING = "pending", // 待审核
    APPROVED = "approved", // 已批准
    REJECTED = "rejected", // 已拒绝
    PAID = "paid" // 已支付
}

// 文件类型枚举
enum FileType {
    PDF = "pdf",
    WORD = "word",
    EXCEL = "excel",
    IMAGE = "image",
    OTHER = "other"
}

// 文件信息接口
interface FileInfo {
    url: string;
    name: string;
    type: FileType;
    uploadedAt: Date;
}

// 从URL中获取文件类型
const getFileType = (url: string): FileType => {
    try {
        // 从URL中提取文件名（去除查询参数）
        const urlWithoutParams = url.split('?')[0];
        const fileName = urlWithoutParams.split('/').pop() || '';

        // 检查文件扩展名
        if (fileName.toLowerCase().includes('.pdf')) {
            return FileType.PDF;
        } else if (fileName.toLowerCase().includes('.doc') || fileName.toLowerCase().includes('.docx')) {
            return FileType.WORD;
        } else if (fileName.toLowerCase().includes('.xls') || fileName.toLowerCase().includes('.xlsx')) {
            return FileType.EXCEL;
        } else if (
            fileName.toLowerCase().includes('.jpg') ||
            fileName.toLowerCase().includes('.jpeg') ||
            fileName.toLowerCase().includes('.png') ||
            fileName.toLowerCase().includes('.gif') ||
            fileName.toLowerCase().includes('.webp')
        ) {
            return FileType.IMAGE;
        }

        // 如果URL中包含文件类型信息
        if (url.toLowerCase().includes('content-type=application/pdf')) {
            return FileType.PDF;
        } else if (url.toLowerCase().includes('content-type=application/msword') ||
            url.toLowerCase().includes('content-type=application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            return FileType.WORD;
        } else if (url.toLowerCase().includes('content-type=application/vnd.ms-excel') ||
            url.toLowerCase().includes('content-type=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
            return FileType.EXCEL;
        } else if (url.toLowerCase().includes('content-type=image/')) {
            return FileType.IMAGE;
        }

        // 默认返回其他类型
        return FileType.OTHER;
    } catch (error) {
        console.error('Error determining file type:', error);
        return FileType.OTHER;
    }
};

// 从文件对象获取文件类型
const getFileTypeFromFile = (file: File): FileType => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    if (fileName.endsWith('.pdf') || mimeType === 'application/pdf') {
        return FileType.PDF;
    } else if (
        fileName.endsWith('.doc') || 
        fileName.endsWith('.docx') || 
        mimeType === 'application/msword' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
        return FileType.WORD;
    } else if (
        fileName.endsWith('.xls') || 
        fileName.endsWith('.xlsx') || 
        mimeType === 'application/vnd.ms-excel' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
        return FileType.EXCEL;
    } else if (
        fileName.endsWith('.jpg') || 
        fileName.endsWith('.jpeg') || 
        fileName.endsWith('.png') || 
        fileName.endsWith('.gif') || 
        fileName.endsWith('.webp') || 
        mimeType.startsWith('image/')
    ) {
        return FileType.IMAGE;
    }
    
    return FileType.OTHER;
};

// 从URL中提取文件名
const getFileNameFromUrl = (url: string): string => {
    try {
        // 从URL中提取文件名（去除查询参数）
        const urlWithoutParams = url.split('?')[0];
        const fileName = urlWithoutParams.split('/').pop() || '未知文件';
        // 解码URL编码的文件名
        return decodeURIComponent(fileName);
    } catch (error) {
        console.error('Error extracting filename from URL:', error);
        return '未知文件';
    }
};

interface ReimbursementFormData {
    title: string;
    description: string;
    amount: number;
    type: ReimbursementType;
    receipts: FileInfo[];
    additionalDocuments?: FileInfo[];
    status: ReimbursementStatus;
}

// 预览文件接口
interface PreviewFile {
    url: string;
    type: FileType;
    name: string;
}

const ReimbursementForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [type, setType] = useState<ReimbursementType>(ReimbursementType.TEACHING_MATERIALS);
    const [receipts, setReceipts] = useState<File[]>([]);
    const [existingReceipts, setExistingReceipts] = useState<FileInfo[]>([]);
    const [additionalDocs, setAdditionalDocs] = useState<File[]>([]);
    const [existingAdditionalDocs, setExistingAdditionalDocs] = useState<FileInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEdit, setIsEdit] = useState(false);
    const [receiptPreviews, setReceiptPreviews] = useState<PreviewFile[]>([]);
    const [additionalDocPreviews, setAdditionalDocPreviews] = useState<PreviewFile[]>([]);

    // 如果有ID参数，则加载现有报销记录
    useEffect(() => {
        const fetchReimbursement = async () => {
            if (!id || !auth.currentUser) return;

            try {
                setLoading(true);
                const docRef = doc(db, 'reimbursements', id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setTitle(data.title || '');
                    setDescription(data.description || '');
                    setAmount(data.amount?.toString() || '');
                    setType(data.type || ReimbursementType.TEACHING_MATERIALS);
                    
                    // 处理收据数据
                    if (data.receipts) {
                        // 如果是旧格式（字符串数组），则转换为新格式
                        if (typeof data.receipts[0] === 'string') {
                            const convertedReceipts = data.receipts.map((url: string) => ({
                                url,
                                name: getFileNameFromUrl(url),
                                type: getFileType(url),
                                uploadedAt: data.updatedAt?.toDate() || new Date()
                            }));
                            setExistingReceipts(convertedReceipts);
                        } else {
                            // 已经是新格式
                            setExistingReceipts(data.receipts.map((receipt: any) => ({
                                ...receipt,
                                uploadedAt: receipt.uploadedAt?.toDate() || new Date()
                            })));
                        }
                    }
                    
                    // 处理附加文档数据
                    if (data.additionalDocuments) {
                        // 如果是旧格式（字符串数组），则转换为新格式
                        if (typeof data.additionalDocuments[0] === 'string') {
                            const convertedDocs = data.additionalDocuments.map((url: string) => ({
                                url,
                                name: getFileNameFromUrl(url),
                                type: getFileType(url),
                                uploadedAt: data.updatedAt?.toDate() || new Date()
                            }));
                            setExistingAdditionalDocs(convertedDocs);
                        } else {
                            // 已经是新格式
                            setExistingAdditionalDocs(data.additionalDocuments.map((doc: any) => ({
                                ...doc,
                                uploadedAt: doc.uploadedAt?.toDate() || new Date()
                            })));
                        }
                    }
                    
                    setIsEdit(true);
                }
            } catch (err) {
                console.error('获取报销记录失败:', err);
                setError(t('reimbursements.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchReimbursement();
    }, [id, t]);

    const handleReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setReceipts(prev => [...prev, ...newFiles]);

            // 创建预览URL并存储文件类型信息
            newFiles.forEach(file => {
                const url = URL.createObjectURL(file);
                const fileType = getFileTypeFromFile(file);
                setReceiptPreviews(prev => [...prev, { 
                    url, 
                    type: fileType,
                    name: file.name
                }]);
            });
        }
    };

    const handleAdditionalDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setAdditionalDocs(prev => [...prev, ...newFiles]);
            
            // 创建预览URL并存储文件类型信息
            newFiles.forEach(file => {
                const url = URL.createObjectURL(file);
                const fileType = getFileTypeFromFile(file);
                setAdditionalDocPreviews(prev => [...prev, { 
                    url, 
                    type: fileType,
                    name: file.name
                }]);
            });
        }
    };

    const handleRemoveReceipt = (index: number) => {
        setReceipts(prev => prev.filter((_, i) => i !== index));
        setReceiptPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveAdditionalDoc = (index: number) => {
        setAdditionalDocs(prev => prev.filter((_, i) => i !== index));
        setAdditionalDocPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveExistingReceipt = (index: number) => {
        setExistingReceipts(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveExistingAdditionalDoc = (index: number) => {
        setExistingAdditionalDocs(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            setError(null);

            const user = auth.currentUser;
            if (!user) {
                throw new Error(t('common.loginRequired'));
            }

            // 上传新的收据图片
            const uploadReceiptPromises = receipts.map(async (file) => {
                const storageRef = ref(storage, `reimbursements/${user.uid}/receipts/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                
                // 返回文件信息对象而不仅仅是URL
                return {
                    url,
                    name: file.name,
                    type: getFileTypeFromFile(file),
                    uploadedAt: new Date()
                };
            });

            // 上传新的附加文档
            const uploadAdditionalDocPromises = additionalDocs.map(async (file) => {
                const storageRef = ref(storage, `reimbursements/${user.uid}/additionalDocs/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                
                // 返回文件信息对象而不仅仅是URL
                return {
                    url,
                    name: file.name,
                    type: getFileTypeFromFile(file),
                    uploadedAt: new Date()
                };
            });

            const newReceiptInfos = await Promise.all(uploadReceiptPromises);
            const newAdditionalDocInfos = await Promise.all(uploadAdditionalDocPromises);

            // 合并现有和新上传的文件信息
            const allReceipts = [...existingReceipts, ...newReceiptInfos];
            const allAdditionalDocs = [...existingAdditionalDocs, ...newAdditionalDocInfos];

            const reimbursementData: ReimbursementFormData = {
                title,
                description,
                amount: parseFloat(amount),
                type,
                receipts: allReceipts,
                additionalDocuments: allAdditionalDocs,
                status: ReimbursementStatus.PENDING
            };

            if (isEdit && id) {
                // 更新现有记录
                await updateDoc(doc(db, 'reimbursements', id), {
                    ...reimbursementData,
                    updatedAt: Timestamp.now()
                });
            } else {
                // 创建新记录
                const userRef = doc(db, 'users', user.uid);
            await addDoc(collection(db, 'reimbursements'), {
                ...reimbursementData,
                    createdBy: userRef,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
            });
            }

            // 返回上一页
            navigate(-1);
        } catch (err) {
            console.error('提交报销申请失败:', err);
            setError(t('reimbursements.submitError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:px-8 mt-32">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {isEdit ? t('reimbursements.edit') : t('reimbursements.create')}
                        </h3>
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            {t('common.back')}
                        </button>
                    </div>
                </div>
                <div className="border-t border-gray-200">
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {error && (
                            <div className="text-red-600 text-sm">{error}</div>
                        )}
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.title')}
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.amount')}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.type')}
                            </label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value as ReimbursementType)}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                            >
                                <option value={ReimbursementType.TEACHING_MATERIALS}>{t('reimbursements.types.teaching_materials')}</option>
                                <option value={ReimbursementType.TRAVEL}>{t('reimbursements.types.travel')}</option>
                                <option value={ReimbursementType.OFFICE_SUPPLIES}>{t('reimbursements.types.office_supplies')}</option>
                                <option value={ReimbursementType.EQUIPMENT}>{t('reimbursements.types.equipment')}</option>
                                <option value={ReimbursementType.OTHER}>{t('reimbursements.types.other')}</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.description')}
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.receipts')}
                            </label>
                            <div className="mt-1">
                                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                                    <div className="space-y-1 flex flex-col items-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <div className="flex text-sm text-gray-600">
                                            <label htmlFor="receipt-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                                <span>{t('reimbursements.uploadReceipts')}</span>
                                                <input
                                                    id="receipt-upload"
                                                    name="receipt-upload"
                                                    type="file"
                                                    className="sr-only"
                                                    accept="image/*,.pdf"
                                                    multiple
                                                    onChange={handleReceiptChange}
                                                />
                                            </label>
                                            <p className="pl-1">{t('reimbursements.dragAndDrop')}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">{t('reimbursements.receiptsHelp')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 显示现有收据 */}
                        {existingReceipts.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('reimbursements.existingReceipts')}</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {existingReceipts.map((fileInfo, index) => (
                                        <div key={index} className="relative group">
                                            <div className="h-32 w-full border border-gray-300 rounded-md overflow-hidden shadow-sm">
                                                {fileInfo.type === FileType.PDF ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                        <svg className="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={fileInfo.url}
                                                        alt={`收据 ${index + 1}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExistingReceipt(index)}
                                                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full z-10 p-1.5 shadow-sm hover:bg-red-200 transition-colors"
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                            <a
                                                href={fileInfo.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all"
                                            >
                                                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-2 py-1 bg-black bg-opacity-50 rounded">
                                                    {t('reimbursements.preview')}
                                                </span>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 显示新上传收据预览 */}
                        {receiptPreviews.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('reimbursements.newReceipts')}</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {receiptPreviews.map((preview, index) => (
                                        <div key={index} className="relative group">
                                            <div className="h-32 w-full border border-gray-300 rounded-md overflow-hidden shadow-sm bg-gray-50">
                                                {preview.type === FileType.PDF ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                        <svg className="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{preview.name}</span>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={preview.url}
                                                        alt={`新收据 ${index + 1}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <div className="absolute top-0 right-0 p-1 bg-white bg-opacity-75 rounded-bl">
                                                <span className="text-xs text-blue-600 font-medium">{t('reimbursements.new')}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveReceipt(index)}
                                                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1.5 shadow-sm hover:bg-red-200 transition-colors"
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('reimbursements.additionalDocuments')} ({t('common.optional')})
                            </label>
                            <div className="mt-1">
                                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-green-400 transition-colors">
                                    <div className="space-y-1 flex flex-col items-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <div className="flex text-sm text-gray-600">
                                            <label htmlFor="additional-doc-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500">
                                                <span>{t('reimbursements.uploadDocuments')}</span>
                            <input
                                                    id="additional-doc-upload"
                                                    name="additional-doc-upload"
                                type="file"
                                                    className="sr-only"
                                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                multiple
                                                    onChange={handleAdditionalDocChange}
                                                />
                                            </label>
                                            <p className="pl-1">{t('reimbursements.dragAndDrop')}</p>
                                        </div>
                                        <p className="text-xs text-gray-500">{t('reimbursements.additionalDocumentsHelp')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 显示现有附加文档 */}
                        {existingAdditionalDocs.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('reimbursements.existingDocuments')}</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {existingAdditionalDocs.map((fileInfo, index) => (
                                        <div key={index} className="relative group">
                                            <div className="h-32 w-full border border-gray-300 rounded-md overflow-hidden shadow-sm">
                                                {fileInfo.type === FileType.PDF ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                        <svg className="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    </div>
                                                ) : fileInfo.type === FileType.WORD ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-blue-50">
                                                        <svg className="h-10 w-10 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">Word</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    </div>
                                                ) : fileInfo.type === FileType.EXCEL ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-green-50">
                                                        <svg className="h-10 w-10 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">Excel</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{fileInfo.name}</span>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={fileInfo.url}
                                                        alt={`文档 ${index + 1}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExistingAdditionalDoc(index)}
                                                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1.5 shadow-sm hover:bg-red-200 transition-colors"
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                            <a
                                                href={fileInfo.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all"
                                            >
                                                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-2 py-1 bg-black bg-opacity-50 rounded">
                                                    {t('reimbursements.preview')}
                                                </span>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 显示新上传附加文档预览 */}
                        {additionalDocPreviews.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('reimbursements.newDocuments')}</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {additionalDocPreviews.map((preview, index) => (
                                        <div key={index} className="relative group">
                                            <div className="h-32 w-full border border-gray-300 rounded-md overflow-hidden shadow-sm">
                                                {preview.type === FileType.PDF ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                                                        <svg className="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">PDF</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{preview.name}</span>
                                                    </div>
                                                ) : preview.type === FileType.WORD ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-blue-50">
                                                        <svg className="h-10 w-10 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">Word</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{preview.name}</span>
                                                    </div>
                                                ) : preview.type === FileType.EXCEL ? (
                                                    <div className="flex flex-col items-center justify-center h-full bg-green-50">
                                                        <svg className="h-10 w-10 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="mt-2 text-sm text-gray-500">Excel</span>
                                                        <span className="mt-1 text-xs text-gray-400 truncate max-w-full px-2">{preview.name}</span>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={preview.url}
                                                        alt={`新文档 ${index + 1}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <div className="absolute top-0 right-0 p-1 bg-white bg-opacity-75 rounded-bl">
                                                <span className="text-xs text-green-600 font-medium">{t('reimbursements.new')}</span>
                                            </div>
                                        <button
                                            type="button"
                                                onClick={() => handleRemoveAdditionalDoc(index)}
                                                className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1.5 shadow-sm hover:bg-red-200 transition-colors"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-5">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                {loading ? t('common.submitting') : (isEdit ? t('common.update') : t('common.submit'))}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReimbursementForm; 